<template>
  <section>
    <PageTitle>Visiting Record</PageTitle>
    <PageContent>
      <form @submit.prevent="submit" novalidate>
        <Container>
          <NoticeBox type="error" v-if="hasErrors">
            Please correct the specified fields
          </NoticeBox>

          <VisitCustomer
            :name="visit.customer_name"
            :email="visit.email"
            :cmc-no="visit.cmc_no"
          />

          <section class="section">
            <Headline :level="2">Visiting details</Headline>
            <div class="form-group required">
              <label>Visit Date 来店日</label>
              <Datepicker 
                v-model="visit.use_datetime" 
                :class="{ 'has-error': hasError('visit.use_datetime') }"/>
              <FormError :errors="errors['visit.use_datetime'] || []" />
            </div>
            <div class="form-group required">
              <label>Venue 利用店舗</label>
              <div class="select is-fullwidth">
                <select
                  class="form-control"
                  :class="{ 'has-error': hasError('visit.use_facility_id') }"
                  v-model.number="visit.use_facility_id"
                  @change="onFacilityChange"
                >
                  <option value="0">Select a venue</option>
                  <option
                    v-for="item in facilities"
                    :key="item.use_facility_id"
                    :value="item.use_facility_id"
                    v-html="item.use_facility_name"
                  />
                </select>
              </div>
              <FormError :errors="errors['visit.use_facility_id'] || []" />
              <template v-if="visit.use_facility_id !== 0">
                <div class="select space-t-2 is-fullwidth">
                  <select
                    class="form-control"
                    :class="{ 'has-error': hasError('visit.use_child_facility_id') }"
                    v-model.number="visit.use_child_facility_id"
                    :disabled="isFacilityLoading"
                  >
                    <option value="0">Select a place</option>
                    <option
                      v-for="item in childFacilities"
                      :key="item.use_child_facility_id"
                      :value="item.use_child_facility_id"
                      v-html="item.use_child_facility_name"
                    />
                  </select>
                </div>
                <FormError :errors="errors['visit.use_child_facility_id'] || []" />
              </template>
            </div>
            <div class="form-group">
              <label>Number of guests ご利用人数</label>
              <div class="select block">
                <select
                  class="form-control"
                  :class="{ 'has-error': hasError('visit.count_of_people') }"
                  v-model.number="visit.count_of_people"
                >
                  <option value="0">Select a number</option>
                  <option v-for="n in 10" :value="n" :key="n">
                    {{ n }}
                  </option>
                  <option :value="11">more than 11</option>
                </select>
              </div>
              <FormError :errors="errors['visit.count_of_people'] || []" />
            </div>
            <div class="form-group">
              <label>Amount paid お支払金額</label>
              <input
                id="bill_amount"
                type="number"
                class="form-control"
                :class="{ 'has-error': hasError('visit.bill_amount') }"
                v-model.number="visit.bill_amount"
                placeholder="Enter a number"
                pattern="\d*"
              />
              <FormError :errors="errors['visit.bill_amount'] || []" />
            </div>
            <div class="form-group">
              <label>Purpose ご利用用途</label>
              <div class="select block">
                <select
                  class="form-control"
                  :class="{ 'has-error': hasError('visit.use_purpose_id') }"
                  v-model.number="visit.use_purpose_id"
                >
                  <option value="0">Select a purpose</option>
                  <option
                    v-for="data in usePurpose"
                    :value="data.use_purpose_id"
                    :key="`purpose-${data.use_purpose_id}`"
                    v-html="data.use_purpose_name"
                  />
                </select>
              </div>
              <FormError :errors="errors['visit.use_purpose_id'] || []" />
            </div>
            <div class="form-group">
              <label>Remarks 備考</label>
              <textarea
                class="form-control is-multiline"
                placeholder="Enter text"
                v-model="visit.remarks"
                maxlength="1000"
                :rows="visit.remarks.split(/\n/).length"
              />
            </div>
            <div class="form-footer">
              <div class="text-center">
                <WaveButton type="submit" class="button" :loading="loading">
                  Save changes
                </WaveButton>
              </div>
              <div class="text-center space-t-3">
                <router-link :to="visit.cmc_no | useHistoryPath">
                  View history
                </router-link>
              </div>
            </div>
          </section>
        </Container>
      </form>

      <Modal
        id="complate-update"
        title="Visiting record was saved"
        icon="check"
        @ok="modalOk"
      />
    </PageContent>
  </section>
</template>

<script>
import withEmpty from '@/filters/with-empty'
import Modal from '@/components/Modal.vue'
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import VisitCustomer from '@/components/VisitCustomer.vue'
import usePurposeApi from '@/api/use-purpose'
import facilityApi from '@/api/use-facility'
import visitApi from '@/api/visit'
import { renderError, isNotFound } from '@/utils/error'
import validationMixin from '@/mixins/validation'
import Datepicker from '@/components/Datepicker.vue'

export default {
  components: {
    Modal,
    NoticeBox,
    FormError,
    VisitCustomer,
    Datepicker
  },

  metaInfo() {
    return {
      title: this.$store.getters['app/getMenuName']('visit'),
      bodyAttrs: { class: 'visit-show' }
    }
  },

  filters: {
    withEmpty,
    useHistoryPath(cmcNo) {
      return { name: 'use-history.index', query: { u: cmcNo } }
    }
  },

  mixins: [
    validationMixin
  ],

  rules: {
    'visit.use_datetime': ['来店日', [
      ['required', '', 'Please enter the visit date']
    ]],
    'visit.use_facility_id': ['利用店舗', [
      ['required', { deny: [0] }, 'Please enter the venue']
    ]],
    'visit.use_child_facility_id': ['利用施設', [
      ['required', { deny: [0] }, 'Please enter the place']
    ]],
    'visit.bill_amount': ['お支払い金額（割引後）', [
      ['digits', 9, 'Please enter the discounted amount within 9 digits']
    ]],
  },

  data() {
    return {
      visit: {
        use_facility_id: 0,
        use_facility_name: '',
        use_child_facility_id: 0,
        use_child_facility_name: '',
        use_purpose_id: 0,
        use_purpose_name: '',
        count_of_people: 0,
        bill_amount: '',
        revision: '',
        use_datetime: '',
        email: '',
        customer_name: '',
        cmc_no: '',
        remarks: '',
      },
      usePurpose: [],
      facilities: [],
      childFacilities: [],
      token: '',
      loading: false,
      fromUseHistory: false,
      isFacilityLoading: false
    }
  },

  computed: {
    historyId() {
      return this.$route.params.historyId
    },
    visitorId() {
      return this.$route.params.visitorId
    },
    backLink() {
      const route = { name: 'use-history.index' }

      return this.fromUseHistory
        ? Object.assign({}, route, { query: { q: 'last' } })
        : route
    },
    isFacilityInvalid() {
      return !this.facilities.some(f => {
        return f.use_facility_id === this.visit.use_facility_id
      })
    },
    isPurposeInvalid() {
      return !this.usePurpose.some(p => {
        return p.use_purpose_id === this.visit.use_purpose_id
      })
    }
  },

  methods: {
    async fetchVisit() {
      try {
        this.visit = await visitApi.show(this.visitorId, this.historyId)
      } catch(err) {
        renderError(err)
      }
    },
    async fetchUsePurpose() {
      try {
        this.usePurpose = await usePurposeApi.get()
      } catch(err) {
        renderError(err)
      }
    },
    async fetchUseFacility() {
      try {
        this.facilities = await facilityApi.get()
      } catch(err) {
        renderError(err)
      }
    },
    async onFacilityChange() {
      if (this.isFacilityLoading) {
        return false
      }

      this.isFacilityLoading = true

      this.visit.use_child_facility_id = 0

      if (this.visit.use_facility_id === 0) {
        this.isFacilityLoading = false
        return false
      }

      try {
        this.childFacilities = await facilityApi.getChildren(
          this.visit.use_facility_id
        )
        this.$nextTick(() => (this.isFacilityLoading = false))
      } catch(err) {
        this.isFacilityLoading = false
        // 無効な施設
        if (isNotFound(err)) {
          this.query.use_child_facility_id = 0
          return false
        }
        renderError(err)
      }
    },
    async submit() {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        const { revision } = await visitApi.update(
          this.visitorId,
          this.historyId,
          this.visit
        )

        this.visit.revision = revision

        this.$modal.show('complate-update')
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    },

    modalOk() {
      this.$modal.close('complate-update')
    }
  },

  async created() {
    this.$withLoading(async () => {
      await this.fetchVisit()

      await Promise.all([
        this.fetchUsePurpose(),
        this.fetchUseFacility(),
      ])

      // 無効な利用用途
      if (this.isPurposeInvalid) {
        this.visit.use_purpose_id = 0
      }

      // 無効な店舗
      if (this.isFacilityInvalid) {
        this.visit.use_facility_id = 0
        this.visit.use_child_facility_id = 0
        return false
      }

      const childId = this.visit.use_child_facility_id
      await this.onFacilityChange()
      this.visit.use_child_facility_id = childId
    })
  },

  watch: {
    loading(val) {
      this.canSubmit = !val
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromUseHistory = from.name === 'use-history.index'
    })
  }
}
</script>
